import * as Sentry from '@sentry/browser'
import { IToastProps } from '@blueprintjs/core'
import Client from '../client'
import { establishQZConnection } from '../current/actions'
import { PrintSettings } from '../current/types'
import store from '../store'
import { stringifyParams } from '../utils/pagination'
import {
  DEFAULT_DYMO_CONFIG,
  DEFAULT_LABEL_SIZE,
  DEFAULT_SHIPPING_LABEL_SIZE,
  DEFAULT_STAR_ESCPOS_CONFIG,
  DEFAULT_STAR_PDF_CONFIG,
  DEFAULT_ZEBRA_CONFIG,
  LISTER_CONNECT_CONNECTION_FAILURE,
  LISTER_CONNECT_NO_CURRENT_PRINTER,
  MEDIA_TICKET_SIZE
} from './constants'
import qz from './qz'
import PrinterToaster from './toaster'
import { IProductLabelParams, IMediaTicketParams, PrintLabelFormat } from './types'

function clearAndToast(props: IToastProps) {
  PrinterToaster.clear()
  PrinterToaster.show(props)
}

class PrinterV2 {
  currentPrinter: string

  currentPrinterType: PrintLabelFormat

  printSettings: PrintSettings

  listerConnectEnabled: boolean

  apiToken: string

  constructor(newTabPdfPrinter = false) {
    const currState = store.getState()

    this.listerConnectEnabled = currState.current.organization.settings.listerConnectEnabled
    this.printSettings = currState.current.organization.settings.printSettings
    this.apiToken = currState.current.user.apiToken

    if (newTabPdfPrinter) {
      this.currentPrinter = 'newTabPdfPrinter'
      this.currentPrinterType = 'PDF'
    } else {
      this.currentPrinter = currState.current.currentPrinter
      this.currentPrinterType = currState.current.currentPrinterType
    }
  }

  track(event: string) {
    // sample amount of events so we don't bombard Amplitude with data
    if (Math.random() <= 0.1) {
      analytics.track(`Print ${event}`, {
        printerType: this.currentPrinterType,
        printer: this.currentPrinter
      })
    }
  }

  async canPrint(): Promise<boolean> {
    if (this.currentPrinter === 'newTabPdfPrinter') {
      return true
    }

    const res = await store.dispatch<any>(establishQZConnection())
    if (!res) {
      clearAndToast(LISTER_CONNECT_CONNECTION_FAILURE)

      return false
    }

    if (!this.currentPrinter) {
      clearAndToast(LISTER_CONNECT_NO_CURRENT_PRINTER)

      return false
    }

    return true
  }

  async print(config: any, data: {}[]): Promise<boolean> {
    if (this.currentPrinter === 'newTabPdfPrinter') {
      data.forEach(o => window.open((o as any).data))
    }

    try {
      await qz.print(config, data)
      PrinterToaster.show({
        message: `Label sent to ${this.currentPrinter} (${this.currentPrinterType})`,
        intent: 'primary'
      })

      return true
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
      Sentry.captureException(e)
      return false
    }
  }

  /**
   * Merges raw print params with the organizations defaults
   */
  buildRawQueryString(params: IProductLabelParams): string {
    const { printSettings } = this

    const paramsToSerialize: IProductLabelParams = { sku: params.sku }

    if (printSettings.productLabelSupplierName) {
      paramsToSerialize.supplierName = params.supplierName
    }
    if (printSettings.productLabelCurrentDate) {
      paramsToSerialize.date = params.date
    }
    if (printSettings.productLabelInventoryLocationName) {
      paramsToSerialize.inventoryLocationName = params.inventoryLocationName
    }
    if (printSettings.productLabelName) {
      paramsToSerialize.productName = params.productName
    }

    return stringifyParams({ ...paramsToSerialize, apiToken: this.apiToken })
  }

  // Reusing IProductLabelParams interface as the label params match the required manifest params.
  async printManifestLabelRaw(labelConfigs: IProductLabelParams[]): Promise<boolean> {
    const canProceed = await this.canPrint()
    if (!canProceed) return false
    if (labelConfigs.length === 0) return true

    this.track('Manifest Label')

    let config
    let data

    if (this.currentPrinterType === 'ZPL') {
      config = qz.configs.create(this.currentPrinter, { ...DEFAULT_ZEBRA_CONFIG })
      data = labelConfigs.map(c => ({
        type: 'raw',
        format: 'command',
        flavor: 'file',
        data: `/print/labels/products.zpl?${this.buildRawQueryString(c)}`
      }))
    } else {
      config = qz.configs.create(this.currentPrinter, {
        ...DEFAULT_DYMO_CONFIG,
        size: DEFAULT_LABEL_SIZE
      })
      data = labelConfigs.map(c => ({
        type: 'pixel',
        format: 'pdf',
        flavor: 'file',
        data: `/print/labels/products.pdf?${this.buildRawQueryString(c)}`
      }))
    }

    return this.print(config, data)
  }

  async printProductLabelRaw(labelConfigs: IProductLabelParams[]): Promise<boolean> {
    const canProceed = await this.canPrint()
    if (!canProceed) return false
    if (labelConfigs.length === 0) return true

    this.track('Product Label')

    let config
    let data

    if (this.currentPrinterType === 'ZPL') {
      config = qz.configs.create(this.currentPrinter, { ...DEFAULT_ZEBRA_CONFIG })
      data = labelConfigs.map(c => ({
        type: 'raw',
        format: 'command',
        flavor: 'file',
        data: `/print/labels/products.zpl?${this.buildRawQueryString(c)}`
      }))
    } else {
      config = qz.configs.create(this.currentPrinter, {
        ...DEFAULT_DYMO_CONFIG,
        size: DEFAULT_LABEL_SIZE
      })
      data = labelConfigs.map(c => ({
        type: 'pixel',
        format: 'pdf',
        flavor: 'file',
        data: `/print/labels/products.pdf?${this.buildRawQueryString(c)}`
      }))
    }

    return this.print(config, data)
  }

  async printProductLabelFromId(productIds: number[]): Promise<boolean> {
    const canProceed = await this.canPrint()
    if (!canProceed) return false

    let config
    let data
    const qs = `?api_token=${this.apiToken}`

    this.track('Product Label')

    if (this.currentPrinterType === 'ZPL') {
      config = qz.configs.create(this.currentPrinter, { ...DEFAULT_ZEBRA_CONFIG })
      data = productIds.map(id => ({
        type: 'raw',
        format: 'command',
        flavor: 'file',
        data: `/print/labels/products/${id}.zpl${qs}`
      }))
    } else {
      config = qz.configs.create(this.currentPrinter, {
        ...DEFAULT_DYMO_CONFIG,
        size: DEFAULT_LABEL_SIZE
      })
      data = productIds.map(id => ({
        type: 'pixel',
        format: 'pdf',
        flavor: 'file',
        data: `/print/labels/products/${id}.pdf${qs}`
      }))
    }

    return this.print(config, data)
  }

  async printMediaTicket(query: string): Promise<boolean> {
    const canProceed = await this.canPrint()
    if (!canProceed) return false

    this.track('Media Ticket')

    let config
    let data

    if (this.currentPrinterType === 'ESC') {
      config = qz.configs.create(this.currentPrinter, { ...DEFAULT_STAR_ESCPOS_CONFIG, size: MEDIA_TICKET_SIZE })
      data = [
        {
          type: 'raw',
          format: 'command',
          flavor: 'file',
          data: `/print/labels/media_ticket.esc?${query}`
        }
      ]
    } else if (this.currentPrinterType === 'ZPL') {
      config = qz.configs.create(this.currentPrinter, { ...DEFAULT_ZEBRA_CONFIG })
      data = [
        {
          type: 'raw',
          format: 'command',
          flavor: 'file',
          data: `/print/labels/media_ticket.zpl?${query}`
        }
      ]
    } else {
      // PDF
      config = qz.configs.create(this.currentPrinter, { ...DEFAULT_STAR_PDF_CONFIG, size: MEDIA_TICKET_SIZE })
      data = [
        {
          type: 'pixel',
          format: 'pdf',
          flavor: 'file',
          data: `/print/labels/media_ticket.pdf?${query}`
        }
      ]
    }
    return this.print(config, data)
  }

  async printMediaTicketFromId(inventoryUnitId: number): Promise<boolean> {
    const query = stringifyParams({ inventoryUnitId, apiToken: this.apiToken })
    return this.printMediaTicket(query)
  }

  async printMediaTicketRaw(attrs: IMediaTicketParams): Promise<boolean> {
    const query = stringifyParams({ ...attrs, apiToken: this.apiToken })
    return this.printMediaTicket(query)
  }

  async printInventoryLabelFromId(inventoryLocationIds: number[]): Promise<boolean> {
    const canProceed = await this.canPrint()
    if (!canProceed) return false

    let config
    let data
    const qs = `?api_token=${this.apiToken}`

    this.track('Inventory Label')
    if (this.currentPrinterType === 'ZPL') {
      config = qz.configs.create(this.currentPrinter, { ...DEFAULT_ZEBRA_CONFIG })
      data = inventoryLocationIds.map(id => ({
        type: 'raw',
        format: 'command',
        flavor: 'file',
        data: `/print/labels/inventory/${id}.zpl${qs}`
      }))
    } else {
      config = qz.configs.create(this.currentPrinter, {
        ...DEFAULT_DYMO_CONFIG,
        size: DEFAULT_LABEL_SIZE
      })
      data = inventoryLocationIds.map(id => ({
        type: 'pixel',
        format: 'pdf',
        flavor: 'file',
        data: `/print/labels/inventory/${id}.pdf${qs}`
      }))
    }

    return this.print(config, data)
  }

  async printShippingLabelFromShipmentId(shipmentId: number) {
    const canProceed = await this.canPrint()
    if (!canProceed) return

    this.track('Shipping Label')

    if (this.currentPrinterType === 'ZPL') {
      // must be converted to ZPL before printing
      const newLabel = await Client.convertLabel(shipmentId, this.currentPrinterType.toLowerCase())
      const config = qz.configs.create(this.currentPrinter, { ...DEFAULT_ZEBRA_CONFIG })
      this.print(config, [{ type: 'raw', format: 'command', flavor: 'file', data: newLabel.data.labelUrl }])
      return
    }

    const newLabel = await Client.convertLabel(shipmentId, 'png')
    const config = qz.configs.create(this.currentPrinter, {
      ...DEFAULT_DYMO_CONFIG,
      scaleContent: 'true',
      size: DEFAULT_SHIPPING_LABEL_SIZE
    })

    this.print(config, [{ type: 'pixel', format: 'image', flavor: 'file', data: newLabel.data.labelUrl }])
  }

  /**
   * This will read a static test file and print it
   * Only use this for testing printing is working
   */
  async printTestShippingLabel() {
    const canProceed = await this.canPrint()
    if (!canProceed) return

    if (this.currentPrinterType === 'ZPL') {
      const config = qz.configs.create(this.currentPrinter, { ...DEFAULT_ZEBRA_CONFIG })
      this.print(config, [{ type: 'raw', format: 'command', flavor: 'file', data: `/labels/test/shipping.zpl` }])
      return
    }

    const config = qz.configs.create(this.currentPrinter, {
      ...DEFAULT_DYMO_CONFIG,
      scaleContent: 'true',
      size: DEFAULT_SHIPPING_LABEL_SIZE
    })

    this.print(config, [{ type: 'pixel', format: 'image', flavor: 'file', data: `/labels/test/shipping.png` }])
  }
}

export default PrinterV2
